import { useEffect, useRef } from 'react';
import { Box } from '@mui/material';
import { useEditor } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import Link from '@tiptap/extension-link';
import Underline from '@tiptap/extension-underline';
import TextAlign from '@tiptap/extension-text-align';
import Placeholder from '@tiptap/extension-placeholder';
import Color from '@tiptap/extension-color';
import { TextStyle } from '@tiptap/extension-text-style';
import Subscript from '@tiptap/extension-subscript';
import Superscript from '@tiptap/extension-superscript';
import Image from '@tiptap/extension-image';
import { Table } from '@tiptap/extension-table';
import TableRow from '@tiptap/extension-table-row';
import TableHeader from '@tiptap/extension-table-header';
import TableCell from '@tiptap/extension-table-cell';
import {
  MenuButtonBold,
  MenuButtonItalic,
  MenuButtonUnderline,
  MenuButtonStrikethrough,
  MenuButtonBulletedList,
  MenuButtonOrderedList,
  MenuButtonBlockquote,
  MenuButtonHorizontalRule,
  MenuButtonUndo,
  MenuButtonRedo,
  MenuButtonSubscript,
  MenuButtonSuperscript,
  MenuButtonTextColor,
  MenuButtonAddImage,
  MenuButtonAddTable,
  MenuControlsContainer,
  MenuDivider,
  MenuSelectHeading,
  MenuButtonEditLink,
  RichTextEditorProvider,
  RichTextField,
  LinkBubbleMenu,
  LinkBubbleMenuHandler,
  TableBubbleMenu,
} from 'mui-tiptap';

interface SummaryRichTextEditorProps {
  /** Initial HTML content to load into the editor */
  initialContent?: string;
  /** Callback fired when editor content changes – receives HTML string */
  onChange: (html: string) => void;
  /** Placeholder text when the editor is empty */
  placeholder?: string;
  /** Minimum height of the editor area */
  minHeight?: number;
}

export default function SummaryRichTextEditor({
  initialContent = '',
  onChange,
  placeholder = 'Write your market summary here…',
  minHeight = 250,
}: SummaryRichTextEditorProps) {
  const onChangeRef = useRef(onChange);
  onChangeRef.current = onChange;

  const editor = useEditor({
    extensions: [
      StarterKit.configure({
        heading: { levels: [2, 3, 4] },
      }),
      Link.configure({
        openOnClick: false,
        autolink: true,
      }),
      LinkBubbleMenuHandler,
      Underline,
      TextStyle,
      Color,
      Subscript,
      Superscript.extend({ excludes: 'subscript' }),
      TextAlign.configure({ types: ['heading', 'paragraph'] }),
      Placeholder.configure({ placeholder }),
      Image.configure({ allowBase64: false }),
      Table.configure({ resizable: true }),
      TableRow,
      TableHeader,
      TableCell,
    ],
    content: initialContent,
    onUpdate: ({ editor: e }) => {
      onChangeRef.current(e.getHTML());
    },
  });

  // Sync external content changes (e.g. when dialog opens with new initialData)
  useEffect(() => {
    if (editor && !editor.isDestroyed && !editor.isFocused) {
      const currentContent = editor.getHTML();
      if (initialContent !== currentContent) {
        editor.commands.setContent(initialContent);
      }
    }
  }, [initialContent, editor]);

  if (!editor) return null;

  const handleAddImage = () => {
    const url = window.prompt('Enter image URL');
    if (url) {
      editor.chain().focus().setImage({ src: url }).run();
    }
  };

  return (
    <Box
      sx={{
        '& .MuiTiptap-FieldContainer-root': {
          borderColor: 'divider',
        },
        '& .MuiTiptap-RichTextField-content': {
          minHeight,
          '& .tiptap.ProseMirror': {
            minHeight,
            padding: 2,
            // Table styles inside the editor
            '& table': {
              borderCollapse: 'collapse',
              width: '100%',
              my: 1,
            },
            '& th, & td': {
              border: '1px solid',
              borderColor: 'divider',
              p: 1,
              minWidth: 60,
            },
            '& th': {
              bgcolor: 'action.hover',
              fontWeight: 600,
            },
            '& img': {
              maxWidth: '100%',
              height: 'auto',
              borderRadius: 1,
            },
          },
          // Placeholder styling
          '& p.is-editor-empty:first-of-type::before': {
            color: 'text.secondary',
            fontStyle: 'italic',
          },
        },
      }}
    >
      <RichTextEditorProvider editor={editor}>
        <RichTextField
          variant="outlined"
          controls={
            <MenuControlsContainer>
              <MenuSelectHeading />
              <MenuDivider />
              <MenuButtonBold />
              <MenuButtonItalic />
              <MenuButtonUnderline />
              <MenuButtonStrikethrough />
              <MenuButtonSubscript />
              <MenuButtonSuperscript />
              <MenuDivider />
              <MenuButtonTextColor
                defaultTextColor="#000000"
                swatchColors={[
                  { value: '#000000', label: 'Black' },
                  { value: '#E53935', label: 'Red' },
                  { value: '#1E88E5', label: 'Blue' },
                  { value: '#43A047', label: 'Green' },
                  { value: '#FB8C00', label: 'Orange' },
                  { value: '#8E24AA', label: 'Purple' },
                  { value: '#546E7A', label: 'Grey' },
                ]}
              />
              <MenuDivider />
              <MenuButtonBulletedList />
              <MenuButtonOrderedList />
              <MenuDivider />
              <MenuButtonBlockquote />
              <MenuButtonHorizontalRule />
              <MenuDivider />
              <MenuButtonEditLink />
              <MenuButtonAddImage onClick={handleAddImage} />
              <MenuButtonAddTable />
              <MenuDivider />
              <MenuButtonUndo />
              <MenuButtonRedo />
            </MenuControlsContainer>
          }
        />
        <LinkBubbleMenu />
        <TableBubbleMenu />
      </RichTextEditorProvider>
    </Box>
  );
}
